import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { FiDownload, FiMaximize, FiMinimize, FiX } from "react-icons/fi";
import Default from "../../components/layouts/default";
import FilePreviewRenderer from "../../components/filepreview/FilePreviewRenderer";
import FilesService from "../../services/files.service";
import { getFileIcon } from "../../utils/fileIcon";

/**
 * Landing page for an attachment link in a chat email.
 *
 * The link carries the message id and the file id, nothing else. The API checks
 * that the file really is one of that message's attachments and that this portal
 * user is on that thread, so a forwarded or guessed link gets a refusal rather
 * than a file. ProtectedRoutes keeps an unauthenticated visitor out until they
 * have logged in, and sends them back here afterwards.
 *
 * Laid out like the Teams file manager: the message's files on the left, the
 * selected one rendered on the right, so a message with several attachments is
 * read without going back to the email for each one.
 */

const VIEW_HEIGHT = 620;

// Room for the full-screen panel's own header, so the viewer gets the rest.
const FULLSCREEN_CHROME = 60;

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

const FilePreview = () => {
  const { messageId, fileId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [files, setFiles] = useState([]);
  const [subject, setSubject] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [selectedId, setSelectedId] = useState(fileId ? String(fileId) : null);
  const [preview, setPreview] = useState({ url: null, type: null, name: null });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const [fullscreen, setFullscreen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window === "undefined" ? 800 : window.innerHeight
  );

  // The object URL currently on screen, so it can be revoked when it is replaced
  // or the page closes - blobs stay in memory until they are.
  const previewUrlRef = useRef(null);

  const releasePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  // A 401 means the session lapsed between the guard letting us in and the
  // request going out: log in once, come straight back to this file.
  const toLogin = () => {
    const back = encodeURIComponent(location.pathname + location.search);
    navigate(`/login?redirect=${back}`, { replace: true });
  };

  const messageOf = (err, fallback) => {
    const status = err?.response?.status;
    if (status === 403) return "You do not have access to this file.";
    if (status === 404) return "This file is no longer available.";
    return fallback;
  };

  /* The left-hand list: every file on the message the link came from */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setListLoading(true);
        setListError("");
        const res = await FilesService.messageAttachments(messageId);
        if (cancelled) return;
        setFiles(res.data.files || []);
        setSubject(res.data.subject || "");
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) return toLogin();
        setListError(messageOf(err, "Failed to load the attachments of this message."));
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  /* The selected file, fetched as a blob and handed to the renderer */
  useEffect(() => {
    // Nothing selected: the preview was closed, so let its blob go.
    if (!selectedId) {
      releasePreviewUrl();
      setPreview({ url: null, type: null, name: null });
      setPreviewError("");
      return undefined;
    }

    // The list has to arrive first: the renderer picks its viewer from the name
    // as well as the content type, and fetching before the names are known would
    // cost a second request for the same file.
    if (listLoading) return undefined;

    let cancelled = false;

    (async () => {
      try {
        setPreviewLoading(true);
        setPreviewError("");
        releasePreviewUrl();
        setPreview({ url: null, type: null, name: null });

        const res = await FilesService.messageAttachmentPreview(messageId, selectedId);
        if (cancelled) return;

        const blobUrl = URL.createObjectURL(res.data);
        previewUrlRef.current = blobUrl;

        const listed = files.find((f) => String(f.id) === String(selectedId));
        setPreview({
          url: blobUrl,
          type: res.headers["content-type"] || listed?.contentType || "",
          name: listed?.fileName || "",
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) return toLogin();
        setPreviewError(messageOf(err, "Failed to load the file preview."));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, selectedId, listLoading]);

  /* Release the last blob when the page closes */
  useEffect(() => releasePreviewUrl, []);

  /* Full screen is sized from the window, so it follows a resize */
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fullscreen]);

  // The selection lives in the URL, so the address bar always names the file on
  // screen and the back button walks the ones already opened.
  const selectFile = (id) => {
    if (String(id) === String(selectedId)) return;
    navigate(`/file-preview/${messageId}/${id}`);
  };

  // Closing drops the file from the URL and leaves the list, the way closing the
  // preview panel does in Teams.
  const closePreview = () => {
    setFullscreen(false);
    navigate(`/file-preview/${messageId}`);
  };

  // The URL is the source of truth for what is open, so back, forward and the
  // close above all land in the same place.
  useEffect(() => {
    setSelectedId(fileId ? String(fileId) : null);
    if (!fileId) setFullscreen(false);
  }, [fileId]);

  /* Escape steps back one level: out of full screen, or out of the preview */
  useEffect(() => {
    if (!selectedId) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (fullscreen) setFullscreen(false);
      else closePreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen, selectedId]);

  const downloadFile = () => {
    if (!preview.url) return;
    const link = document.createElement("a");
    link.href = preview.url;
    link.download = preview.name || "attachment";
    link.click();
  };

  const rendererHeight = fullscreen
    ? Math.max(viewportHeight - FULLSCREEN_CHROME, 300)
    : VIEW_HEIGHT;

  const previewActions = preview.url ? (
    <div className="cp-preview-actions">
      <button type="button" title="Download" aria-label="Download" onClick={downloadFile}>
        <FiDownload size={18} />
      </button>
      <button
        type="button"
        title={fullscreen ? "Exit full screen" : "Full screen"}
        aria-label={fullscreen ? "Exit full screen" : "Full screen"}
        onClick={() => setFullscreen(!fullscreen)}
      >
        {fullscreen ? <FiMinimize size={18} /> : <FiMaximize size={18} />}
      </button>
      {/* From full screen this steps back to the split view rather than closing
          outright - going full screen and back should leave you where you were,
          not discard the preview you had open. Same rule as Escape. */}
      <button
        type="button"
        title={fullscreen ? "Back to split view (Esc)" : "Close preview (Esc)"}
        aria-label={fullscreen ? "Back to split view" : "Close preview"}
        onClick={() => (fullscreen ? setFullscreen(false) : closePreview())}
      >
        <FiX size={19} />
      </button>
    </div>
  ) : null;

  const previewBody = previewLoading ? (
    <div className="text-center text-muted py-5">
      <Spinner animation="border" size="sm" className="me-2" />
      Loading preview...
    </div>
  ) : previewError ? (
    <div className="text-center text-danger py-5">{previewError}</div>
  ) : preview.url ? (
    <FilePreviewRenderer
      // Remounted when the pane resizes: the viewers measure their container
      // once, on the way in, so a size change has to give them a fresh start.
      key={`${preview.url}-${fullscreen ? "fs" : "side"}`}
      fileUrl={preview.url}
      fileType={preview.type}
      fileName={preview.name}
      height={rendererHeight}
    />
  ) : (
    <div className="text-center text-muted py-5">Select a file to preview it.</div>
  );

  return (
    <Default pageTitle="File Preview" pageSubTitle={subject}>
      <style>{`
        .cp-preview-actions {
          position: absolute;
          top: 16px;
          right: 24px;
          /* Above the spreadsheet viewer, which stacks its own chrome up to
             1001 - at any lower value its grid swallows the clicks and the
             buttons look enabled but do nothing. */
          z-index: 1100;
          pointer-events: auto;
          display: flex;
          align-items: center;
          gap: 1px;
          padding: 2px;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid #e0e3e6;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
          backdrop-filter: blur(2px);
        }
        .cp-preview-actions button {
          border: 0;
          background: transparent;
          border-radius: 4px;
          padding: 6px 8px;
          line-height: 0;
          color: #5a6570;
          cursor: pointer;
          transition: background 0.12s, color 0.12s;
        }
        .cp-preview-actions button:hover {
          background: #eef0f2;
          color: #212529;
        }
        .cp-preview-fullscreen {
          position: fixed;
          inset: 0;
          z-index: 1080;
          border-radius: 0;
          display: flex;
          flex-direction: column;
        }
      `}</style>

      {/* Full screen: the panel leaves the grid and covers the page, so the
          document gets the whole window rather than a column of it. */}
      {fullscreen && (
        <div className="card cp-preview-fullscreen">
          <div className="card-header d-flex align-items-center gap-2">
            <span
              className="fw-semibold text-truncate"
              style={{ fontSize: "0.9rem" }}
              title={preview.name || ""}
            >
              {preview.name || "Preview"}
            </span>
          </div>
          <div className="card-body p-0" style={{ position: "relative", flex: 1, minHeight: 0 }}>
            {previewActions}
            {previewBody}
          </div>
        </div>
      )}

      <div className="row g-3">
        {/* Left: every file on this message. Sized to its own rows - no h-100,
            so it does not follow the height of whatever is being previewed. */}
        <div className="col-12 col-lg-3 align-self-start">
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: "0.9rem" }}>
              Attachments {files.length > 0 && `(${files.length})`}
            </div>
            <div
              className="list-group list-group-flush"
              style={{ maxHeight: VIEW_HEIGHT, overflowY: "auto" }}
            >
              {listLoading ? (
                <div className="text-center text-muted py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading...
                </div>
              ) : listError ? (
                <div className="text-danger text-center small py-4">{listError}</div>
              ) : files.length === 0 ? (
                <div className="text-muted text-center small py-4">
                  This message has no attachments.
                </div>
              ) : (
                files.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => selectFile(file.id)}
                    className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${
                      String(file.id) === String(selectedId) ? "active" : ""
                    }`}
                    style={{ fontSize: "0.8rem" }}
                  >
                    <img
                      src={getFileIcon(file.fileName)}
                      alt=""
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <span className="flex-grow-1 text-truncate text-start" title={file.fileName}>
                      {file.fileName}
                    </span>
                    <span className="flex-shrink-0 small opacity-75">
                      {formatSize(file.fileSize)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: the selected file, drawn by the same viewers the Teams app
            uses. Stays mounted but hidden while full screen shows the same
            content, so returning to the split view is instant. */}
        <div className="col-12 col-lg-9" style={{ visibility: fullscreen ? "hidden" : "visible" }}>
          <div className="card h-100">
            <div className="card-header d-flex align-items-center gap-2">
              <span
                className="fw-semibold text-truncate"
                style={{ fontSize: "0.9rem" }}
                title={preview.name || ""}
              >
                {preview.name || "Preview"}
              </span>
            </div>
            <div className="card-body" style={{ position: "relative" }}>
              {!fullscreen && previewActions}
              {!fullscreen && previewBody}
            </div>
          </div>
        </div>
      </div>
    </Default>
  );
};

export default FilePreview;
